// src/lib/sectionStore.ts
//
// Shared, reactive "active section per module" store so the ONE global app
// sidebar can drive a module's sub-tab selection live (replacing each module's
// own desktop section rail — that duplication was the "double side menu"). The
// sidebar writes setSection(moduleKey, section); the module reads it via
// useSection and renders that section. Keyed by the module's Page value.

import { useSyncExternalStore } from 'react';

const state: Record<string, string> = {};
const listeners = new Set<() => void>();
let version = 0;

function emit() { version++; listeners.forEach((l) => l()); }

/** Re-render trigger for components (e.g. the sidebar) that read getSection() directly. */
export function useSectionsVersion(): number {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => version, () => version,
  );
}

export function setSection(moduleKey: string, section: string): void {
  if (state[moduleKey] === section) return;
  state[moduleKey] = section;
  emit();
}

export function getSection(moduleKey: string): string | undefined {
  return state[moduleKey];
}

/** Reactive [section, setSection] for a module; falls back until something is set. */
export function useSection(moduleKey: string, fallback: string): [string, (s: string) => void] {
  const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
  const val = useSyncExternalStore(subscribe, () => state[moduleKey], () => state[moduleKey]);
  return [val ?? fallback, (s: string) => setSection(moduleKey, s)];
}
